import { useQuery } from "@tanstack/react-query";

import { AccountCreate, getRanaApiV1 } from "@nens/rana/api-v1";

import { useMutationWithErrorHandler } from "../../hooks/use-mutation-with-error-handler";
import { isErrorResponse } from "../../lib/utils/get-errors";

export function useInvitationCheck(tenantId: string, invitationId?: string) {
  return useQuery({
    queryKey: [tenantId, "invitations", invitationId, "check"],
    queryFn: async () => {
      const apiV1 = getRanaApiV1();
      apiV1.setTenantId(tenantId);
      return await apiV1.InvitationsApi.invitationCheck({
        // invitationId is already checked in enabled
        invitationId: invitationId + "",
      });
    },
    enabled: !!invitationId,
  });
}

export function useCreateAccountMutation(tenant: string) {
  return useMutationWithErrorHandler(async (accountCreate: AccountCreate) => {
    const apiV1 = getRanaApiV1();
    apiV1.setTenantId(tenant);
    return await apiV1.AccountsApi.accountCreate({ accountCreate });
  });
}

type UserPutArgs = {
  id: string;
  givenName: string;
  familyName: string;
  email: string;
  invitationId: string | null | undefined;
  tenant: string;
  enabled: boolean;
};

// This is the only hook of its type in Rana that combines a mutation with a query.
// This is because the mutation is used to trigger a backend process that updates the user information,
// and needs to be called every time we open the rana platform. That mutation then also lets us know if the user
// actually has access to the tenant or not, which is needed to decide if we should log the user in or not.
export function useUserPutMutationQuery({
  id,
  givenName,
  familyName,
  email,
  invitationId,
  tenant,
  enabled,
}: UserPutArgs) {
  const { mutateAsync, errors, isError } = useMutationWithErrorHandler(
    async () =>
      await getRanaApiV1().UsersApi.userPut({
        userPut: {
          invitationId,
          givenName,
          familyName,
          email,
        },
        id,
      }),

    {
      retry: (failureCount, error) => {
        const shouldRetry =
          isErrorResponse(error) && error.response.status === 401 && failureCount < 2;

        if (isErrorResponse(error)) {
          const status = error.response.status;
          // Retry on 401 Unauthenticated error
          // Sometimes there is a race condition where the frontend
          // is still waiting for a new token from the backend,
          // but the user Put is already sent using the old token.
          // This results in a 401 Unauthenticated error.
          // https://github.com/nens/rana/issues/1561
          return status === 401 && failureCount < 2;
        }
        return false;
      },
    }
  );

  // Get isSuccess from the query because isSuccess is only true when the mutation has been called.
  // But if if the component rerenders (hot-reload) and the query state knows the mutation has already been called,
  // isSuccess on the mutation will be false, but on the query it will be true.
  const userPutQuery = useQuery({
    queryKey: ["user-put", id, tenant],
    queryFn: async () => await mutateAsync(),
    enabled,
    staleTime: Infinity,
  });

  const { isSuccess, isFetching } = userPutQuery;
  const isBusy = userPutQuery.isPending || userPutQuery.isFetching;

  return { errors, isError, isSuccess, isBusy };
}
